/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useStandort } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useStandort/useStandort';
import { type Tage } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/tage';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useTranslation } from 'react-i18next';
import z from 'zod';

export function useBezahlungSchema() {
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();
  const { standort } = useStandort(currentStandort.id);
  const BezahlungsartSchema = z
    .object({
      art: z
        .enum(['fixgehalt', 'stunden'])
        .nullable()
        .refine((val) => !!val, {
          error: t(
            'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.art.validationError',
          ),
        }),
      monatsgehalt: z
        .string()
        .max(12, {
          message: t(
            'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.monatsgehalt.validationError',
          ),
        })
        .nullable(),
      tarifGruppe: z.string().nullable(),
      stundenlohn: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.art === 'fixgehalt' || data.art === 'stunden') {
        if (!data.stundenlohn) {
          ctx.addIssue({
            code: 'custom',
            path: ['stundenlohn'],
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.stundenlohn.validationError',
            ),
          });
        }
        if (
          standort?.tarifvertrag?.tarifvertragTyp === 'MANTEL_SYSGASTRO_BDS' &&
          !data.tarifGruppe
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['tarifGruppe'],
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.tarifgruppe.validationError',
            ),
          });
        }
      }
    });

  const ArbeitszeitkontoSchema = z
    .object({
      art: z.enum(['gehaltsempfaenger', 'stundenloehner']).nullable(),
      arbeitstage: z.custom<Tage[keyof Tage]>().array(),
      tagessoll: z.string().nullable(),
      gehaltsempfaenger: z.object({
        sollstunden: z.boolean().nullable(),
        stundenpensum: z.enum(['monat', 'woche']).nullable(),
        berechnungsartMonat: z.enum(['durchschnitt', 'exakt']).nullable(),
        monatsstunden: z.string().nullable(),
        unbezahlteUeberstundenMonat: z.string().nullable(),
        wochenstunden: z.string().nullable(),
        unbezahlteUeberstundenWoche: z.string().nullable(),
      }),
      stundenloehner: z.object({
        auszahlungsgrenze: z.boolean().nullable(),
        richtwert: z.boolean().nullable(),
        auszahlungsgrenzeEinheit: z.enum(['euro', 'stunden']).nullable(),
        auszahlungsgrenzeEuro: z.string().nullable(),
        auszahlungsgrenzeStunden: z.string().nullable(),
        richtwertEinheit: z.enum(['euro', 'stunden', 'exakt']).nullable(),
        richtwertEuro: z.string().nullable(),
        richtwertStunden: z.string().nullable(),
      }),
    })
    .superRefine((data, ctx) => {
      const gehaltsempfaenger = data.gehaltsempfaenger;
      const stundenloehner = data.stundenloehner;

      if (data.art === 'gehaltsempfaenger') {
        if (gehaltsempfaenger.sollstunden === null) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.sollstunden.validationError',
            ),
            path: ['gehaltsempfaenger', 'sollstunden'],
          });
        }
        if (gehaltsempfaenger.sollstunden && !gehaltsempfaenger.stundenpensum) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.stundenpensum.validationError',
            ),
            path: ['gehaltsempfaenger', 'stundenpensum'],
          });
        }
        if (gehaltsempfaenger.stundenpensum === 'monat') {
          if (!gehaltsempfaenger.berechnungsartMonat) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.berechnungsartMonat.validationError',
              ),
              path: ['gehaltsempfaenger', 'berechnungsartMonat'],
            });
          }
          if (gehaltsempfaenger.berechnungsartMonat === 'durchschnitt') {
            if (!gehaltsempfaenger.monatsstunden) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.monatsstunden.validationError',
                ),
                path: ['gehaltsempfaenger', 'monatsstunden'],
              });
            }
          } else if (gehaltsempfaenger.berechnungsartMonat === 'exakt') {
            if (data.arbeitstage.length === 0) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.arbeitstage.validationError',
                ),
                path: ['arbeitstage'],
              });
            }
            if (!data.tagessoll) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.tagessoll.validationError',
                ),
                path: ['tagessoll'],
              });
            }
          }
        } else if (gehaltsempfaenger.stundenpensum === 'woche') {
          if (!gehaltsempfaenger.wochenstunden) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.wochenstunden.validationError',
              ),
              path: ['gehaltsempfaenger', 'wochenstunden'],
            });
          }
        }
      }

      if (
        data.art === 'stundenloehner' &&
        stundenloehner?.auszahlungsgrenze === false
      ) {
        if (stundenloehner.richtwert === null) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.richtwert.validationError',
            ),
            path: ['stundenloehner', 'richtwert'],
          });
        }
      } else if (
        data.art === 'stundenloehner' &&
        stundenloehner?.auszahlungsgrenze === true
      ) {
        if (!stundenloehner.auszahlungsgrenzeEinheit) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.auszahlungsgrenzeEinheit.validationError',
            ),
            path: ['stundenloehner', 'auszahlungsgrenzeEinheit'],
          });
        } else if (stundenloehner.auszahlungsgrenzeEinheit === 'euro') {
          if (stundenloehner.auszahlungsgrenzeEuro === null) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.auszahlungsgrenzeEuro.validationError',
              ),
              path: ['stundenloehner', 'auszahlungsgrenzeEuro'],
            });
          }
        } else if (stundenloehner.auszahlungsgrenzeEinheit === 'stunden') {
          if (stundenloehner.auszahlungsgrenzeStunden === null) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.auszahlungsgrenzeStunden.validationError',
              ),
              path: ['stundenloehner', 'auszahlungsgrenzeStunden'],
            });
          }
        }
      }
      if (data.art === 'stundenloehner' && stundenloehner?.richtwert === true) {
        if (!stundenloehner.richtwertEinheit) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.richtwertEinheit.validationError',
            ),
            path: ['stundenloehner', 'richtwertEinheit'],
          });
        } else {
          if (
            stundenloehner.richtwertEinheit === 'euro' &&
            stundenloehner.richtwertEuro === null
          ) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.richtwertEuro.validationError',
              ),
              path: ['stundenloehner', 'richtwertEuro'],
            });
          } else if (
            stundenloehner.richtwertEinheit === 'stunden' &&
            stundenloehner.richtwertStunden === null
          ) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.richtwertStunden.validationError',
              ),
              path: ['stundenloehner', 'richtwertStunden'],
            });
          } else if (stundenloehner.richtwertEinheit === 'exakt') {
            if (data.arbeitstage.length === 0) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.arbeitstage.validationError',
                ),
                path: ['arbeitstage'],
              });
            }
            if (!data.tagessoll) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.tagessoll.validationError',
                ),
                path: ['tagessoll'],
              });
            }
          }
        }
      }
    });

  const ZuschlaegeSchema = z
    .object({
      zuschlaege: z
        .boolean()
        .nullable()
        .refine((val) => val !== null, {
          error: t(
            'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.zuschlaege.validationError',
          ),
        }),
      zuschlagsgruppe: z.string().nullable(),
      pauschaleZuschlaege: z.string().nullable(),
      uebertariflicheZahlung: z.string().nullable(),
      /**
       * Dieses Feld wurde nur hinzugefügt um die TypeInferenz ohne strict mode zu gewährleisten, da die restlichen Felder alle optional sind. Es sollte nicht in der UI verwendet werden.
       * TODO: Entfernen sobald strict mode aktiv ist
       * */
      strict: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.zuschlaege) {
        if (!(
          data.zuschlagsgruppe ||
          data.pauschaleZuschlaege ||
          data.uebertariflicheZahlung
        )) {
          ctx.addIssue({
            code: 'custom',
            path: [],
          });
        }
      }
    });
  return {
    BezahlungsartSchema,
    ArbeitszeitkontoSchema,
    ZuschlaegeSchema,
  };
}
